import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { updateTask } from 'actions/tasks';

import { isPersonalTask } from 'utils/task';

import { useCanUser } from 'shared-hooks/permission';

import { PublicPageContext } from 'context/PublicPageContext';

import CanUser from 'components/CanUser';
import TaskAssignees from 'components/TaskAssignees';
import SelectTaskPopout from 'components/select-task-popout';
import LabelPicker from 'components/label-picker';
import LabelsList from 'components/labels-list';
import MilestoneName from 'components/milestone-name';
import SelectMilestonePopout from 'components/select-milestone-popout';
import TaskDates from 'components/TaskDates';
import TaskName from 'components/task-name';
import HelpTooltip from 'components/help-tooltip';
import TaskStoryPoints from 'components/TaskStoryPoints';

import IconUser from 'icons/icon-user';
import IconLabel from 'icons/icon-label';
import IconList from 'icons/icon-list';
import IconCalendarSimple from 'icons/icon-calendar-simple';
import IconTask from 'icons/icon-task';
import IconNumber from 'icons/icon-number';

const TaskControls = ({ id, task, isSmallScreen }) => {
    const { t } = useTranslation(['task', 'common']);
    const isPersonal = isPersonalTask(task);
    const publicPageCtx = useContext(PublicPageContext);
    const canAssignToTask = useCanUser('assignToTask', task.created_by);
    const canEditTask = useCanUser('editTask', task.created_by);
    const isPublic = publicPageCtx?.isPublic === true;

    const handleMilestoneSelect = ({ id: milestoneId }) => {
        if (!task) {
            return;
        }

        if (task.milestone === milestoneId) {
            return;
        }

        updateTask(id, {
            milestone: milestoneId
        });
    }

    const handleTaskSelect = taskId => {
        if (taskId && task.dependency === taskId) {
            return;
        }

        updateTask(id, {
            dependency: taskId
        });
    }

    const handleTaskUntie = () => {
        if (!task.dependency) {
            return;
        }

        updateTask(id, {
            dependency: null
        });
    }

    const handleStoryPointsChange = story_points => {
        if (isNaN(story_points)) {
            return;
        }

        updateTask(id, { story_points });
    }

    const controlAssignees = useMemo(() => {
        if (isPersonal || publicPageCtx?.pageConfig?.tasks?.show_assignees === false) {
            return null;
        }

        if (!task.assignees.length && !canAssignToTask) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconUser />
                    </span>

                    <span className="control-text">
                        { t('common:Assignees') }
                    </span>
                </div>

                <div className="control-value">
                    <TaskAssignees
                        id={id}
                        maxUsersCount={8}
                        addUserText={ !task.assignees?.length ? t('common:Add') : undefined }
                        addBtnClassName={'action-text' + (task.assignees?.length ? ' with-icon' : '')}
                        withTooltip={false}
                    />
                </div>
            </div>
        );
    }, [id, task, isPersonal, canAssignToTask, publicPageCtx, t]);

    const controlLabels = useMemo(() => {
        if (!task.labels?.length && (!canEditTask || isPublic)) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconLabel />
                    </span>

                    <span className="control-text">
                        { t('labels:Tags') }
                    </span>
                </div>


                <CanUser type="editTask" customList={task.created_by}>
                    <LabelPicker
                        taskId={id}
                        assignedLabels={task.labels}
                        xAlign="left"
                        defaultXPosition="right"
                    >
                        <div className="control-value">
                            <LabelsList labels={task.labels} inverted={true}>
                                <span className="action-text">
                                    { t('common:Add') }
                                </span>
                            </LabelsList>
                        </div>
                    </LabelPicker>
                </CanUser>

                <CanUser type="editTask" customList={task.created_by} reverse={true}>
                    <LabelsList labels={task.labels} inverted={true} />
                </CanUser>
            </div>
        );
    }, [id, task, canEditTask, isPublic, t]);

    const controlMilestone = useMemo(() => {
        if (isPersonal || publicPageCtx?.pageConfig?.tasks?.show_tied_milestones === false) {
            return null;
        }

        if (!task.milestone && !canEditTask) {
            return null;
        }

        return (
            <div className="control-inner" key={task.id}>
                <div className="control-label">
                    <span className="control-icon">
                        <IconList />
                    </span>

                    <span className="control-text">
                        { t('common:List') }
                    </span>
                </div>

                <div className="control-value">
                    {
                        task.milestone ? (
                            <MilestoneName id={task.milestone} />
                        ) : (
                            <span className="action-text">{ t('common:Tie') }</span>
                        )
                    }
                </div>

                <CanUser type="editTask" customList={task.created_by}>
                    <SelectMilestonePopout
                        title={ t('actions.changeList.title') }
                        projectId={task.project}
                        selected={task.milestone}
                        onSelect={handleMilestoneSelect}
                        showCreate={true}
                        isList={true}
                        xAlign={isSmallScreen ? 'right' : 'left'}
                        defaultXPosition={isSmallScreen ? 'left' : 'right'}
                    >
                        <div className="popout-toggle" />
                    </SelectMilestonePopout>
                </CanUser>
            </div>
        );
    }, [id, task, isPersonal, canEditTask, publicPageCtx, isSmallScreen, t]);

    const controlDates = useMemo(() => {
        if (!task.due_date && !task.start_date && !task.reminder && !task.rule && !canEditTask) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconCalendarSimple />
                    </span>

                    <span className="control-text">
                        { t('common:Dates') }
                    </span>
                </div>

                <div className="control-value">
                    <TaskDates
                        id={id}
                        dateFormat="fullDateTime"
                        xAlign={isSmallScreen ? 'left' : undefined}
                        defaultXPosition={isSmallScreen ? 'center' : undefined}
                        showQuickOptions={!isSmallScreen}
                    />
                </div>
            </div>
        );
    }, [id, task, canEditTask, isSmallScreen, t]);

    const controlDependency = useMemo(() => {
        if (isPersonal) {
            return null;
        }

        if (!task.dependency && !canEditTask) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconTask />
                    </span>

                    <span className="control-text">
                        { t('common:Dependency') }
                    </span>
                </div>

                <div className="control-value">
                    {
                        task.dependency ? (
                            <TaskName id={task.dependency} />
                        ) : (
                            <span className="action-text">{ t('common:Set') }</span>
                        )
                    }
                </div>

                <CanUser type="editTask" customList={task.created_by}>
                    <SelectTaskPopout
                        title={ t('actions.Set Task Dependency') }
                        excluded={[task.id]}
                        projectId={task.project}
                        selected={task.dependency}
                        milestone={task.milestone}
                        onSelect={handleTaskSelect}
                        onUntie={handleTaskUntie}
                        xAlign={isSmallScreen ? 'left' : 'right'}
                        defaultXPosition={isSmallScreen ? 'right' : 'left'}
                    >
                        <div className="popout-toggle" />
                    </SelectTaskPopout>
                </CanUser>
            </div>
        );
    }, [id, task, canEditTask, isSmallScreen, isPersonal, t]);

    const controlStoryPoints = useMemo(() => {
        if (isPersonal || isPublic) {
            return null;
        }

        if (!task.story_points && !canEditTask) {
            return null;
        }

        return (
            <div className="control-inner">
                <div className="control-label">
                    <span className="control-icon">
                        <IconNumber />
                    </span>

                    <span className="control-text">
                        { t('common:Story Points') }

                        <HelpTooltip>
                            { t('common:storyPoints.help') } <a href="https://help.niftypm.com/en/articles/3827830-using-story-points-to-balance-resources-agile-scrum-workflow" target="_blank" rel="noopener noreferrer">{ t('common:Learn More') }</a>
                        </HelpTooltip>
                    </span>
                </div>

                <div className="control-value">
                    <TaskStoryPoints id={id} onChange={handleStoryPointsChange} />
                </div>
            </div>
        );
    }, [id, task, canEditTask, isPublic, isPersonal, t]);

    const wideControlIndex = isSmallScreen ? 2 : 3;
    const controlItems = [controlAssignees, controlLabels, controlMilestone, controlDependency, controlStoryPoints].filter(Boolean);

    controlItems.splice(wideControlIndex, 0, controlDates);

    return controlItems.map((el, index) => (
        <div
            key={index}
            className={'control' + (index % wideControlIndex === 0 ? ' wider' : (index + 1) % wideControlIndex === 0 ? ' narrower' : '')}
        >
            { el }
        </div>
    ));
}

export default TaskControls;
